import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { 
    formatNumberWithComma, 
    getCategoriesNameById, 
    formatCreatedAtDate 
} from '../../utils/helpers';

// Helper functions
const getBranchName = (branchId, branchNamesData) => {
if (!branchNamesData || !Array.isArray(branchNamesData)) return 'Unknown';
const branch = branchNamesData.find(b => parseInt(b.id) === parseInt(branchId));
return branch ? `${branch.name}` : 'Unknown';
};

const getStockLevelInfo = (balance) => {
    if (balance <= 0) {
        return 'Out of Stock';
    } else if (balance <= 10) {
        return 'Low Stock';
    } else if (balance <= 50) {
        return 'Normal Stock';
    } else {
        return 'High Stock';
    }
};

const getStatusText = (status) => {
    return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to split text into multiple lines (like sample service)
const splitTextIntoLines = (doc, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = doc.getTextWidth(currentLine + ' ' + word);
        if (width < maxWidth) {
            currentLine += ' ' + word;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    lines.push(currentLine);
    return lines;
};

// Manual table drawing function for PDF - UPDATED FOR LANDSCAPE
const drawStocksTable = (doc, data, startY, branchNamesData) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const rowHeight = 12; // Defined here

    // Column widths for landscape mode
    const colWidths = [15, 30, 60, 25, 30, 35, 25, 40];

    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;

    // Check if we need a new page
    if (currentY + neededHeight > pageHeight - 30) {
        doc.addPage('l');
        currentY = margin;
    }

    // Draw table header
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');

    const headers = ['#', 'Stock Date', 'Item Name', 'UOM', 'Balance', 'Stock Level', 'Status', 'Branch'];
    let xPos = margin;

    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

    // Draw header text
    headers.forEach((header, index) => {
        const cellX = xPos + 2;
        doc.text(header, cellX, currentY + 7);
        xPos += colWidths[index];
    });

    currentY += rowHeight;

    // Draw table rows
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);

    data.forEach((row, rowIndex) => {
        // Check for page break
        if (currentY + rowHeight > pageHeight - 30) {
            doc.addPage('l');
            currentY = margin;
            
            // Redraw header on new page
            doc.setFillColor(25, 118, 210);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            xPos = margin;
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 7);
                xPos += colWidths[index];
            });
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 248, 248);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        // Draw cell borders
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        
        // Draw vertical lines
        let verticalX = margin;
        for (let i = 0; i <= colWidths.length; i++) {
            doc.line(verticalX, currentY, verticalX, currentY + rowHeight);
            if (i < colWidths.length) verticalX += colWidths[i];
        }
        
        // Draw horizontal lines
        doc.line(margin, currentY, pageWidth - margin, currentY);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        // Draw row data
        const rowData = row;
        xPos = margin;
        
        rowData.forEach((cell, cellIndex) => {
            // Handle long text
            let displayText = String(cell);
            if (cellIndex === 2 || cellIndex === 7) { // Item Name and Branch columns
                const lines = splitTextIntoLines(doc, displayText, colWidths[cellIndex] - 4);
                if (lines.length > 1) {
                    displayText = lines[0] + '...';
                }
            }
            
            doc.text(displayText, xPos + 2, currentY + 7);
            xPos += colWidths[cellIndex];
        });
        
        currentY += rowHeight;
    });

    return currentY;
};

// Export stock taking to PDF (manual table)
export const exportStockTakingPDF = (stocks, branchNamesData) => {
    return new Promise((resolve, reject) => {
        try {
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });
            
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 20;
            const rowHeight = 12; // Fixed: Define rowHeight here
            let currentY = margin;
            
            // Title section
            doc.setFontSize(20);
            doc.setTextColor(25, 118, 210);
            doc.text('STOCK TAKING FORM', pageWidth / 2, currentY, { align: 'center' });
            currentY += 15;
            
            // Generation details
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            doc.text(`Report Generated: ${new Date().toLocaleString()}`, margin, currentY);
            currentY += 8;
            doc.text(`Total Items: ${stocks.length}`, margin, currentY);
            currentY += 8;

            // Instructions
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text('Instructions:', margin, currentY + 10);
            doc.text('1. Fill in the "Count Figure" column with actual counted quantity', margin + 6, currentY + 20);
            doc.text('2. Note any discrepancies in the "Remarks" column', margin + 6, currentY + 30);
            doc.text('3. Sign and date the completed form', margin + 6, currentY + 40);
            
            currentY += 50;
            
            // Prepare table data
            const tableData = stocks.map((stock, index) => [
                (index + 1).toString(),
                stock.name,
                `${formatNumberWithComma(stock.balance)} ${stock.uom}`,
                '', // Count Figure
                '', // Remarks
                getBranchName(stock.branch_id, branchNamesData)
            ]);
            
            // Draw table
            const colWidthsTaking = [15, 60, 50, 40, 40, 40]; // Adjusted for branch column
            const headersTaking = ['#', 'Stock Name', 'Balance/Quantity', 'Count Figure', 'Remarks', 'Branch'];
            
            // Draw header
            doc.setFillColor(25, 118, 210);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            let xPos = margin;
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            headersTaking.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 7);
                xPos += colWidthsTaking[index];
            });
            
            currentY += rowHeight;
            
            // Draw rows
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
            
            tableData.forEach((row, rowIndex) => {
                if (currentY + rowHeight > pageHeight - 40) {
                    doc.addPage('l');
                    currentY = margin;
                    
                    // Redraw header
                    doc.setFillColor(25, 118, 210);
                    doc.setTextColor(255, 255, 255);
                    doc.setFont(undefined, 'bold');
                    
                    xPos = margin;
                    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
                    
                    headersTaking.forEach((header, index) => {
                        doc.text(header, xPos + 2, currentY + 7);
                        xPos += colWidthsTaking[index];
                    });
                    
                    currentY += rowHeight;
                    doc.setFont(undefined, 'normal');
                    doc.setTextColor(0, 0, 0);
                }
                
                // Alternate row colors
                if (rowIndex % 2 === 0) {
                    doc.setFillColor(248, 248, 248);
                } else {
                    doc.setFillColor(255, 255, 255);
                }
                
                // Draw row background
                doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
                
                // Draw row data
                xPos = margin;
                row.forEach((cell, cellIndex) => {
                    doc.text(cell, xPos + 2, currentY + 7);
                    xPos += colWidthsTaking[cellIndex];
                });
                
                currentY += rowHeight;
            });
            
            // Add signature section
            const finalY = currentY + 10;
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            
            // Signature lines
            doc.line(30, finalY, 80, finalY);
            doc.text('Counter Staff Signature', 30, finalY + 10);
            
            doc.line(110, finalY, 160, finalY);
            doc.text('Supervisor Signature', 110, finalY + 10);
            
            doc.line(190, finalY, 240, finalY);
            doc.text('Date', 190, finalY + 10);
            
            const fileName = `stock_taking_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            
            resolve({ success: true, fileName });
        } catch (error) {
            console.error('Stock taking PDF export failed:', error);
            reject(new Error('Failed to export stock taking PDF: ' + error.message));
        }
    });
};

// Enhanced PDF export for Stocks (Landscape mode - manual table)
export const exportStocksToPDFEnhanced = (stocks, branchNamesData , filters = {}) => {
    return new Promise((resolve, reject) => {
        try {
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });
            
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 20;
            let currentY = margin;
            
            // Title section
            doc.setFontSize(20);
            doc.setTextColor(25, 118, 210);
            doc.text('STOCKS REPORT', pageWidth / 2, currentY, { align: 'center' });
            currentY += 15;
            
            // Generation details
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            doc.text(`Report Generated: ${new Date().toLocaleString()}`, margin, currentY);
            currentY += 8;
            doc.text(`Total Items: ${stocks.length}`, margin, currentY);
            currentY += 8;
            
            // Show branch information if available
            if (stocks.length > 0 && stocks[0].branch_id) {
                const branchName = getBranchName(stocks[0].branch_id, branchNamesData);
                if (branchName !== 'Unknown') {
                    doc.text(`Branch: ${branchName}`, margin, currentY);
                    currentY += 8;
                }
            }
            
            // Apply filters text
            const filterTexts = [];
            if (filters.stock_level) filterTexts.push(`Stock Level: ${filters.stock_level}`);
            if (filters.status) filterTexts.push(`Status: ${getStatusText(filters.status)}`);
            if (filters.dateFrom) filterTexts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
            if (filters.dateTo) filterTexts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
            
            if (filterTexts.length > 0) {
                doc.setFontSize(9);
                doc.setTextColor(80, 80, 80);
                const filtersText = `Filters: ${filterTexts.join(', ')}`;
                const filtersLines = splitTextIntoLines(doc, filtersText, pageWidth - 2 * margin);
                
                filtersLines.forEach(line => {
                    doc.text(line, margin, currentY);
                    currentY += 6;
                });
                currentY += 10;
            }
            
            // Prepare table data
            const tableData = stocks.map((stock, index) => [
                (index + 1).toString(),
                new Date(stock.stock_date).toLocaleDateString(),
                stock.name,
                stock.uom,
                formatNumberWithComma(stock.balance),
                getStockLevelInfo(stock.balance),
                getStatusText(stock.status),
                getBranchName(stock.branch_id, branchNamesData)
            ]);
            
            // Draw the table
            drawStocksTable(doc, tableData, currentY + 10, branchNamesData);
            
            // Add page numbers
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150, 150, 150);
                doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            }
            
            const fileName = `stocks_report_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            
            resolve({ success: true, fileName });
        } catch (error) {
            console.error('Stocks PDF export failed:', error);
            reject(new Error('Failed to export to PDF: ' + error.message));
        }
    });
};

// Manual table function for history
const drawHistoryTable = (doc, data, startY, stock, userNames, branchNamesData) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const rowHeight = 12;

    // Column widths for history in landscape
    const colWidths = [15, 25, 25, 30, 20, 20, 20, 40];
    const headers = ['#', 'Date', 'Action', 'User', 'Old Qty', 'Change', 'New Qty', 'Reason'];

    let currentY = startY;
    
    // Draw header
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    let xPos = margin;
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    currentY += rowHeight;
    
    // Draw rows
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    
    data.forEach((row, rowIndex) => {
        // Check for page break
        if (currentY + rowHeight > pageHeight - 30) {
            doc.addPage('l');
            currentY = margin;
            
            // Redraw header
            doc.setFillColor(25, 118, 210);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            xPos = margin;
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 7);
                xPos += colWidths[index];
            });
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 248, 248);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        // Draw row data
        const rowData = row;
        xPos = margin;
        
        rowData.forEach((cell, cellIndex) => {
            // Set color for change column
            if (cellIndex === 5) {
                const changeValue = parseInt(cell.replace(/[^0-9-]/g, ''));
                if (changeValue > 0) {
                    doc.setTextColor(39, 174, 96); // Green for positive
                } else if (changeValue < 0) {
                    doc.setTextColor(231, 76, 60); // Red for negative
                }
            }
            
            doc.text(cell, xPos + 2, currentY + 7);
            doc.setTextColor(0, 0, 0); // Reset color
            xPos += colWidths[cellIndex];
        });
        
        currentY += rowHeight;
    });

    return currentY;
};

// Export stock history to PDF (manual table)
export const exportStockHistoryPDF = (stock, history, userNames, branchNamesData ) => {
    return new Promise((resolve, reject) => {
        try {
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });
            
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 20;
            let currentY = margin;
            
            // Title section
            doc.setFontSize(20);
            doc.setTextColor(25, 118, 210);
            doc.text('STOCK ADJUSTMENT HISTORY REPORT', pageWidth / 2, currentY, { align: 'center' });
            currentY += 10;
            
            doc.setFontSize(12);
            doc.setTextColor(80, 80, 80);
            doc.text(`For: ${stock.name}`, pageWidth / 2, currentY, { align: 'center' });
            currentY += 15;
            
            // Generation details
            doc.setFontSize(10);
            doc.text(`Report Generated: ${new Date().toLocaleString()}`, margin, currentY);
            currentY += 8;
            doc.text(`Total Adjustments: ${history.length}`, margin, currentY);
            currentY += 8;
            
            // Show branch information if available
            if (stock.branch_id) {
                const branchName = getBranchName(stock.branch_id, branchNamesData);
                if (branchName !== 'Unknown') {
                    doc.text(`Branch: ${branchName}`, margin, currentY);
                    currentY += 8;
                }
            }
            
            currentY += 7;
            
            // Stock Information Section
            doc.setFontSize(11);
            doc.setTextColor(25, 118, 210);
            doc.text('STOCK INFORMATION', margin, currentY);
            currentY += 8;
            
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            
            const infoLines = [
                `Item Name: ${stock.name}`,
                `Current Balance: ${formatNumberWithComma(stock.balance)} ${stock.uom}`,
                `Unit of Measure: ${stock.uom}`,
                `Status: ${getStatusText(stock.status)}`
            ];
            
            infoLines.forEach(line => {
                doc.text(line, margin, currentY);
                currentY += 6;
            });
            
            currentY += 10;
            
            // Prepare table data
            const tableData = history.map((item, index) => [
                (index + 1).toString(),
                formatCreatedAtDate(item.created_at),
                item.action_type.charAt(0).toUpperCase() + item.action_type.slice(1),
                getCategoriesNameById(item.user_id, userNames),
                formatNumberWithComma(item.old_quantity),
                (item.quantity_change >= 0 ? '+' : '') + formatNumberWithComma(item.quantity_change),
                formatNumberWithComma(item.new_quantity),
                item.reason || 'N/A'
            ]);
            
            // Draw table
            drawHistoryTable(doc, tableData, currentY + 5, stock, userNames, branchNamesData);
            
            // Add page numbers
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150, 150, 150);
                doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            }
            
            const fileName = `stock_history_${stock.name}_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            
            resolve({ success: true, fileName });
        } catch (error) {
            console.error('Stock history PDF export failed:', error);
            reject(new Error('Failed to export stock history PDF: ' + error.message));
        }
    });
};

// Client-side Excel export for Stocks (Show all attributes)
export const exportStocksToExcel = (stocks, branchNamesData , filters = {}) => {
    return new Promise((resolve, reject) => {
        try {
            // Prepare data for Excel with all attributes
            const excelData = stocks.map((stock, index) => ({
                'No.': index + 1,
                'ID': stock.id,
                'Stock Date': new Date(stock.stock_date).toLocaleDateString(),
                'Item Name': stock.name,
                'UOM': stock.uom,
                'Quantity': stock.quantity || 0,
                'Balance': stock.balance,
                'Stock Level': getStockLevelInfo(stock.balance),
                'Status': getStatusText(stock.status),
                'Branch': getBranchName(stock.branch_id, branchNamesData), // Branch included
                'Cost Price': stock.cost ? formatNumberWithComma(stock.cost, 2) : '0.00',
                'Selling Price': stock.selling ? formatNumberWithComma(stock.selling, 2) : '0.00',
                'Total Cost': stock.total_cost ? formatNumberWithComma(stock.total_cost, 2) : '0.00',
                'Total Selling': stock.total_selling ? formatNumberWithComma(stock.total_selling, 2) : '0.00',
                'Description': stock.description || '',
                'Notes': stock.notes || '',
                'Created Date': formatCreatedAtDate(stock.created_at),
                'Updated Date': formatCreatedAtDate(stock.updated_at)
            }));

            // Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // Auto-size columns
            const maxWidth = excelData.reduce((widths, row) => {
                Object.keys(row).forEach((key, idx) => {
                    const length = String(row[key]).length;
                    if (!widths[idx] || length > widths[idx]) {
                        widths[idx] = length;
                    }
                });
                return widths;
            }, []);
            
            worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Stocks');

            // Generate Excel file and download
            const fileName = `stocks_export_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(workbook, fileName);

            resolve({ success: true, fileName });
        } catch (error) {
            console.error('Stocks Excel export failed:', error);
            reject(new Error('Failed to export to Excel: ' + error.message));
        }
    });
};

// CSV export for Stocks (Show all attributes)
export const exportStocksToCSV = (stocks, branchNamesData ) => {
    return new Promise((resolve, reject) => {
        try {
            // Prepare headers with all attributes
            const headers = [
                'ID', 'Stock Date', 'Item Name', 'UOM', 'Quantity', 'Balance', 
                'Stock Level', 'Status', 'Branch', 'Cost Price', 'Selling Price', // Branch included
                'Total Cost', 'Total Selling', 'Description', 'Notes',
                'Created Date', 'Updated Date'
            ];
            
            // Prepare CSV data
            const csvData = stocks.map(stock => [
                stock.id,
                `"${new Date(stock.stock_date).toLocaleDateString()}"`,
                `"${stock.name.replace(/"/g, '""')}"`,
                `"${stock.uom.replace(/"/g, '""')}"`,
                stock.quantity || 0,
                stock.balance,
                `"${getStockLevelInfo(stock.balance).replace(/"/g, '""')}"`,
                `"${getStatusText(stock.status).replace(/"/g, '""')}"`,
                `"${getBranchName(stock.branch_id, branchNamesData).replace(/"/g, '""')}"`, // Branch included
                stock.cost ? formatNumberWithComma(stock.cost, 2) : '0.00',
                stock.selling ? formatNumberWithComma(stock.selling, 2) : '0.00',
                stock.total_cost ? formatNumberWithComma(stock.total_cost, 2) : '0.00',
                stock.total_selling ? formatNumberWithComma(stock.total_selling, 2) : '0.00',
                `"${(stock.description || '').replace(/"/g, '""')}"`,
                `"${(stock.notes || '').replace(/"/g, '""')}"`,
                `"${formatCreatedAtDate(stock.created_at)}"`,
                `"${formatCreatedAtDate(stock.updated_at)}"`
            ]);

            // Create CSV content
            const csvContent = [
                headers.join(','),
                ...csvData.map(row => row.join(','))
            ].join('\n');

            // Create download link without FileSaver
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `stocks_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            resolve({ success: true });
        } catch (error) {
            console.error('Stocks CSV export failed:', error);
            reject(new Error('Failed to export to CSV: ' + error.message));
        }
    });
};